import {Injectable, Injector} from '@angular/core';
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {Observable, throwError, EMPTY} from 'rxjs';
import {catchError, switchMap} from 'rxjs/operators';
import {SwitchBoard} from '../services/SwitchBoard';
import {Session} from '../services/Session';
import {LoginService} from '../services/Login.service';

@Injectable({
  providedIn: 'root',
})
export class RequestInterceptor implements HttpInterceptor {
  private isLoggingOut = false;

  constructor(
    // CU-868m49jqy: LoginService is resolved lazily through the Injector, never
    // injected here. It extends TaxplanIQServices, which depends on HttpClient,
    // and HttpClient depends on this interceptor - injecting it directly is a
    // dependency cycle that breaks app bootstrap.
    private switchBoard: SwitchBoard,
    private injector: Injector
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.isPublicEndpoint(request.url)) {
      return next.handle(request);
    }

    if (Session.isSessionExpired()) {
      return this.handleAuthError();
    }

    Session.resetSessionStart();

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (this.isAuthError(error)) {
          return this.renewAndRetry(request, next);
        }
        return throwError(() => error);
      })
    );
  }

  private isPublicEndpoint(url: string): boolean {
    const publicSegments = [
      'login',
      'resetpassword',
      'forgotpassword',
      'signup',
      'logos',
      'createpassword',
      'token/refresh',
      // CU-868m49jqy: the remember-me cookie exchange. It runs from the login
      // page after localStorage was cleared, so isSessionExpired() is always
      // true there (no sessionStart key) and the guard above turned the call
      // into another logout instead of letting it reach the network.
      'session/refresh',
      'handoff',
      'create-new-firm',
      // external taxpayer questionnaire form — no session; the token in the
      // path is the credential (CU-868kg6qux)
      'questionnaire-form',
    ];

    const pathname = this.extractPathname(url).toLowerCase();
    return publicSegments.some(seg => {
      const escaped = seg.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return new RegExp(`(^|/)${escaped}(/|$)`).test(pathname);
    });
  }

  private extractPathname(url: string): string {
    try {
      return new URL(url, window.location.origin).pathname;
    } catch {
      return url.split('?')[0];
    }
  }

  private isAuthError(error: HttpErrorResponse): boolean {
    if (error.status === 401 || error.status === 403) {
      return true;
    }

    if (error.status === 412 && this.isTokenRelatedError(error)) {
      return true;
    }

    return false;
  }

  private isTokenRelatedError(error: HttpErrorResponse): boolean {
    const body = error.error;
    if (body && typeof body === 'object' && body.message) {
      const msg = body.message.toLowerCase();
      return msg.includes('invalid token') ||
        msg.includes('blank token') ||
        msg.includes('invalid claims') ||
        msg.includes('invalid refresh token');
    }
    if (typeof body === 'string') {
      const msg = body.toLowerCase();
      return msg.includes('invalid token') ||
        msg.includes('blank token') ||
        msg.includes('invalid claims') ||
        msg.includes('invalid refresh token');
    }
    return false;
  }

  /**
   * CU-868m49jqy: renew the access token once, then replay the request.
   *
   * A 401 used to end the session immediately. The access token lives 15
   * minutes, so any gap in the workspace refresh timer signed the user out
   * mid-task. Only a failed renewal ends the session now.
   */
  private renewAndRetry(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.isLoggingOut) {
      return EMPTY;
    }

    // Only ever renew and replay a request that carried OUR CURRENT bearer.
    // Two kinds of request reach here without one, and cloning the session JWT
    // onto either would hand it to a host that must never see it:
    //  - SOURCE_TYPE.KNOLLI calls, which TaxplanIQServices builds with
    //    anonymous headers on purpose (claude[bot], PR #763);
    //  - createWithHeader(), which sends `Basic <btoa(token)>` to an arbitrary
    //    URL, so testing only for the presence of a header is not enough
    //    (CodeRabbit, PR #763).
    // Both keep the behaviour they had before this PR: end the session, add no
    // header. An exact match also skips renewal when the token rotated between
    // building the request and its 401. That ends the session, which is what
    // happened before this PR anyway, and is the safe direction here.
    const token = Session.getToken();
    if (!token || request.headers.get('Authorization') !== 'Bearer ' + token) {
      return this.handleAuthError();
    }

    const refreshToken = Session.getRefreshToken();
    const user: any = Session.getUser();
    // LoginService.refreshToken() builds its URL from Session.getUser().id and
    // throws synchronously when the user object is missing.
    if (!refreshToken || !user || !user.id) {
      return this.handleAuthError();
    }

    const loginService = this.injector.get(LoginService);
    return loginService.refreshToken({token, refreshToken}).pipe(
      // This catch must stay BEFORE the switchMap (CodeRabbit, PR #763). After
      // it, the replay's own non-auth errors - a 500 on the retried request -
      // reach it too and become an expiry logout instead of reaching the
      // caller. Here it only ever sees a failed renewal.
      // LoginService.handleError rethrows the response body, not the
      // HttpErrorResponse, so there is no status to inspect. Any failure of
      // the renewal itself means the session is gone.
      catchError(() => this.handleAuthError()),
      switchMap((data: any) => {
        if (!data || !data.token) {
          return this.handleAuthError();
        }
        Session.removeToken();
        Session.setToken(data.token);
        // Session.setRefreshToken is a bare localStorage.setItem, so an absent
        // value persists the STRING "undefined" - truthy, and then sent as the
        // refresh token on the next renewal (CodeRabbit, PR #763). Keep the
        // current one instead; the server still accepts it.
        if (data.refreshToken) {
          Session.setRefreshToken(data.refreshToken);
        }
        // TaxplanIQServices.setHeaders() stamps Authorization when the caller
        // builds the request, so replaying the original object would resend
        // the dead token. Clone it with the new one.
        return next.handle(request.clone({
          setHeaders: {Authorization: 'Bearer ' + data.token}
        })).pipe(
          catchError((retryError: HttpErrorResponse) => {
            // One retry only. A second auth failure ends the session; any
            // other error belongs to the original caller.
            if (this.isAuthError(retryError)) {
              return this.handleAuthError();
            }
            return throwError(() => retryError);
          })
        );
      })
    );
  }

  private handleAuthError(): Observable<never> {
    if (this.isLoggingOut) {
      return EMPTY;
    }

    this.isLoggingOut = true;

    this.switchBoard.onNewToast.next({
      toastId: Math.floor(Math.random() * 1000),
      type: 'info',
      message: 'Your session has expired. Redirecting to login...',
      toastClass: 'toast-info',
      duration: 2000
    });

    setTimeout(() => {
      this.switchBoard.onLogOut.next({});
      setTimeout(() => { this.isLoggingOut = false; }, 1000);
    }, 1500);

    return EMPTY;
  }
}
